import React from 'react'
import {
  AsyncSelect,
  AsyncSelectProps,
  Modal,
  ModalProps,
  Select,
  SelectProps,
} from '@workwhile/ui'
import { GroupBase, StylesConfig } from 'react-select'

/**
 * Modal content sits at z-index 10003. Portaled menus must sit above it.
 * Source: https://github.com/workwhile/ui/blob/c6094ceae9a041c7725010e7039448ac59bea30f/src/Modal/styles.ts#L24
 *
 * Radix Dialog also sets pointer-events:none on the document while open
 * (disableOutsidePointerEvents). Portaled menus inherit that and become
 * unclickable unless we opt back in.
 */
export const MODAL_SELECT_MENU_Z_INDEX = 10004

const mergeModalSelectStyles = <
  Option,
  IsMulti extends boolean,
  Group extends GroupBase<Option>,
>(
  styles?: StylesConfig<Option, IsMulti, Group>
): StylesConfig<Option, IsMulti, Group> => ({
  ...styles,
  menuPortal: (base, props) => {
    const callerStyles = styles?.menuPortal?.(base, props) ?? base
    return {
      ...callerStyles,
      zIndex: MODAL_SELECT_MENU_Z_INDEX,
      pointerEvents: 'auto',
    }
  },
})

const isReactSelectPortalTarget = (target: EventTarget | null) => {
  if (!(target instanceof Element)) {
    return false
  }
  return Boolean(
    target.closest('.react-select__menu') ||
      target.closest('.react-select__menu-portal')
  )
}

type OutsideInteractionEvent = {
  preventDefault: () => void
  target?: EventTarget | null
  detail?: { originalEvent?: Event }
}

/**
 * Prevents Radix Dialog outside-handlers from treating a portaled react-select
 * menu as an outside click (which would dismiss the modal).
 */
export const preventDismissForReactSelect = (
  event: OutsideInteractionEvent
) => {
  const target = event.detail?.originalEvent?.target ?? event.target ?? null
  if (isReactSelectPortalTarget(target)) {
    event.preventDefault()
  }
}

/**
 * Drop-in replacement for `@workwhile/ui` Select when rendered inside a Modal.
 * Applies body-portal, fixed positioning, and Modal-safe menu styles.
 */
export function ModalSelect<
  Option = { label: string; value: unknown },
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: SelectProps<Option, IsMulti, Group>) {
  const { styles, ...rest } = props

  return (
    <Select
      {...rest}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={mergeModalSelectStyles(styles)}
    />
  )
}

/**
 * Drop-in replacement for `@workwhile/ui` AsyncSelect when rendered inside a Modal.
 */
export function ModalAsyncSelect<
  Option = { label: string; value: unknown },
  IsMulti extends boolean = false,
  Group extends GroupBase<Option> = GroupBase<Option>,
>(props: AsyncSelectProps<Option, IsMulti, Group>) {
  const { styles, ...rest } = props

  return (
    <AsyncSelect
      {...rest}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      styles={mergeModalSelectStyles(styles)}
    />
  )
}

/**
 * Drop-in replacement for `@workwhile/ui` Modal when the modal contains
 * ModalSelect / ModalAsyncSelect. Forwards outside-interaction handlers so
 * clicks on portaled menus do not dismiss the dialog.
 *
 * Radix outside-handlers aren't on ModalProps but are forwarded to Dialog.Content.
 */
export const ModalWithSelect = (props: ModalProps) => {
  return (
    <Modal
      {...props}
      {...({
        onPointerDownOutside: preventDismissForReactSelect,
        onInteractOutside: preventDismissForReactSelect,
      } as object)}
    />
  )
}
